'use client';

import { useState, useMemo } from 'react';
import { Popover, PopoverButton, PopoverPanel, Portal } from '@headlessui/react';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';

interface DateRangePickerProps {
  startDate: string; // YYYY-MM-DD format
  endDate: string; // YYYY-MM-DD format
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  label?: string;
  required?: boolean;
  id?: string;
}

export default function DateRangePicker({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  label,
  required,
  id,
}: DateRangePickerProps) {
  const { darkMode } = useDarkMode();

  // Parse the current value
  const currentDate = startDate ? new Date(startDate + 'T00:00:00') : new Date();
  const [viewMonth, setViewMonth] = useState(currentDate.getMonth());
  const [viewYear, setViewYear] = useState(currentDate.getFullYear());
  const [selectingStart, setSelectingStart] = useState(true);

  // Format date for display
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Generate calendar days
  const days = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1);
    const startDateObj = new Date(firstDay);
    startDateObj.setDate(startDateObj.getDate() - startDateObj.getDay()); // Start from Sunday

    const daysArray = [];
    const currentDay = new Date(startDateObj);

    // Helper function to format date as YYYY-MM-DD in local timezone
    const formatLocalDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    for (let i = 0; i < 42; i++) {
      // 6 weeks
      const dateStr = formatLocalDate(currentDay);
      const isCurrentMonth = currentDay.getMonth() === viewMonth;
      const todayStr = formatLocalDate(new Date());
      const isToday = dateStr === todayStr;
      const isStartDate = dateStr === startDate;
      const isEndDate = dateStr === endDate;
      const isInRange = startDate && endDate && dateStr >= startDate && dateStr <= endDate;

      daysArray.push({
        date: dateStr,
        day: currentDay.getDate(),
        isCurrentMonth,
        isToday,
        isStartDate,
        isEndDate,
        isInRange,
      });

      currentDay.setDate(currentDay.getDate() + 1);
    }

    return daysArray;
  }, [viewMonth, viewYear, startDate, endDate]);

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  const handleDateSelect = (dateStr: string) => {
    if (selectingStart) {
      onStartDateChange(dateStr);
      setSelectingStart(false);
    } else {
      onEndDateChange(dateStr);
      setSelectingStart(true);
    }
  };

  const handleClear = (close: () => void) => {
    onStartDateChange('');
    onEndDateChange('');
    setSelectingStart(true);
    close();
  };

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const displayText = () => {
    if (!startDate && !endDate) return 'Select date range';
    if (startDate && !endDate) return `${formatDate(startDate)} - ...`;
    if (!startDate && endDate) return `... - ${formatDate(endDate)}`;
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  return (
    <div>
      {label && (
        <label
          htmlFor={id}
          className={`mb-2 block text-sm/6 font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
        >
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      <Popover className="relative">
        {({ close }) => (
          <>
            <PopoverButton
              id={id}
              className={`block flex w-full items-center justify-between rounded-md px-3 py-1.5 text-base outline-1 -outline-offset-1 transition-all sm:text-sm/6 ${
                darkMode
                  ? 'bg-white/5 text-gray-100 outline-white/10'
                  : 'bg-white text-gray-900 outline-gray-300'
              } ${
                darkMode
                  ? 'focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-500'
                  : 'focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600'
              } `}
            >
              <span>{displayText()}</span>
              <CalendarIcon className="size-5" />
            </PopoverButton>

            <Portal>
              <PopoverPanel
                anchor="bottom start"
                className={`z-[100] mt-2 min-w-[280px] rounded-lg p-4 shadow-xl ring-1 ${
                  darkMode
                    ? 'bg-gray-800 shadow-black/50 ring-white/10'
                    : 'bg-white shadow-black/10 ring-black/5'
                }`}
              >
                {/* Selection Mode Indicator */}
                <div
                  className={`mb-3 text-center text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
                >
                  {selectingStart ? 'Select start date' : 'Select end date'}
                </div>

                {/* Calendar Header */}
                <div className="mb-4 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={handlePrevMonth}
                    className={`rounded p-1 hover:bg-gray-500/10 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
                  >
                    <ChevronLeftIcon className="size-5" />
                  </button>
                  <div
                    className={`text-sm font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}
                  >
                    {monthNames[viewMonth]} {viewYear}
                  </div>
                  <button
                    type="button"
                    onClick={handleNextMonth}
                    className={`rounded p-1 hover:bg-gray-500/10 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
                  >
                    <ChevronRightIcon className="size-5" />
                  </button>
                </div>

                {/* Day Names */}
                <div className="mb-2 grid grid-cols-7 gap-1">
                  {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                    <div
                      key={day}
                      className={`py-2 text-center text-xs font-medium ${
                        darkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}
                    >
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Days */}
                <div className="grid grid-cols-7 gap-1">
                  {days.map((day, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => handleDateSelect(day.date)}
                      className={`rounded py-2 text-sm transition-colors ${!day.isCurrentMonth && (darkMode ? 'text-gray-600' : 'text-gray-400')} ${day.isCurrentMonth && !day.isStartDate && !day.isEndDate && !day.isInRange && (darkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-900 hover:bg-gray-100')} ${(day.isStartDate || day.isEndDate) && 'bg-indigo-600 font-semibold text-white'} ${day.isInRange && !day.isStartDate && !day.isEndDate && (darkMode ? 'bg-indigo-600/30 text-gray-100' : 'bg-indigo-100 text-gray-900')} ${day.isToday && !day.isStartDate && !day.isEndDate && 'font-semibold ring-1 ring-indigo-600'} `}
                    >
                      {day.day}
                    </button>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="mt-4 flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleClear(close)}
                    className={`flex-1 rounded px-3 py-1.5 text-sm transition-colors ${
                      darkMode
                        ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                        : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                    }`}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={() => close()}
                    className={`flex-1 rounded px-3 py-1.5 text-sm transition-colors ${
                      darkMode
                        ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                        : 'bg-indigo-600 text-white hover:bg-indigo-700'
                    }`}
                  >
                    Done
                  </button>
                </div>
              </PopoverPanel>
            </Portal>
          </>
        )}
      </Popover>
    </div>
  );
}
